"use client";

/**
 * Carries checkout context across the sign-in boundary.
 *
 * The auth drawer is global. The Google and Apple buttons inside it fire
 * `auth_provider_selected` with a provider and a platform and nothing else, so
 * the one question the queen funnel needs answering — "of the people who hit
 * the auth wall on THIS event, which method did they pick, and did they come
 * back?" — was unanswerable: 67 provider selections in ten days, none of them
 * attributable to an event.
 *
 * Threading the context through props does not work, because OAuth leaves the
 * document. `sessionStorage` does: it survives the provider round trip, dies
 * with the tab, and is scoped to the one thing being attributed.
 */

export type AuthAttributionSurface = "event" | "venue" | "shop";

export type AuthAttribution = {
  surface: AuthAttributionSurface;
  hiive_slug?: string;
  event_slug?: string;
  occurrence_id?: number | null;
  total_passes?: number;
  /** Set when the provider is chosen, read when the session appears. */
  provider?: "google" | "apple" | "phone";
  /** Epoch ms, so a stale record from a previous purchase can be discarded. */
  started_at: number;
};

const STORAGE_KEY = "hiive:auth-attribution";

/**
 * Sign-in is measured in minutes — an OTP SMS, a provider consent screen, a
 * password manager. Beyond half an hour the record belongs to an abandoned
 * attempt, and attributing a later sign-in to it would invent a conversion.
 */
const MAX_AGE_MS = 30 * 60 * 1000;

export function setAuthAttribution(
  input: Omit<AuthAttribution, "started_at">,
): void {
  if (typeof window === "undefined") return;
  try {
    const value: AuthAttribution = { ...input, started_at: Date.now() };
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value));
  } catch {
    // Storage is blocked in some in-app browsers. The funnel loses the
    // attribution for that visitor; nothing else changes.
  }
}

/** Merge a field into the stored record without clearing what is already there. */
export function updateAuthAttribution(patch: Partial<AuthAttribution>): void {
  const existing = readAuthAttribution();
  if (!existing) return;
  try {
    window.sessionStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...existing, ...patch }),
    );
  } catch {
    // See above.
  }
}

export function readAuthAttribution(): AuthAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<AuthAttribution>;
    if (!parsed.surface || typeof parsed.started_at !== "number") return null;
    if (Date.now() - parsed.started_at > MAX_AGE_MS) {
      clearAuthAttribution();
      return null;
    }
    return parsed as AuthAttribution;
  } catch {
    return null;
  }
}

/**
 * Drop the attribution if the person walked away before choosing a method.
 *
 * Opening the drawer writes the checkout context; a completion is only counted
 * when a session later appears. Left alone, somebody who opened the drawer,
 * changed their mind, and then signed in somewhere else within the half-hour
 * window would be recorded as having completed that checkout's sign-in.
 *
 * A recorded `provider` means they were genuinely mid-flight — a consent screen
 * or an SMS — so that case is deliberately kept.
 */
export function clearAuthAttributionIfUnstarted(): void {
  const existing = readAuthAttribution();
  if (!existing || existing.provider) return;
  clearAuthAttribution();
}

export function clearAuthAttribution(): void {
  if (typeof window === "undefined") return;
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // See above.
  }
}

/**
 * The stored context as event properties, without the bookkeeping fields.
 * Returns an empty object when there is nothing to attribute, so callers can
 * spread it unconditionally.
 */
export function authAttributionProperties(): Record<string, unknown> {
  const attribution = readAuthAttribution();
  if (!attribution) return {};
  const { started_at, ...rest } = attribution;
  return {
    ...rest,
    seconds_since_prompt: Math.round((Date.now() - started_at) / 1000),
  };
}
